import React from 'react';
import PropTypes from 'prop-types';
import { OPERATORS, getAvailableOperators } from './operators';
import DanglingRefRow from './DanglingRefRow';

/**
 * Render addressable field entries as grouped <option>s. Fields sharing a
 * top-level parent group under a single <optgroup> for readability in deep
 * schemas. Paths that ARE a top-level field (length-1) render ungrouped at
 * the bottom of the group (the group label matches the entry itself).
 */
function renderGroupedFieldOptions(fields) {
	// Preserve input order (paths.js already yields container before children).
	const groups = new Map();
	for (const f of fields) {
		const groupKey = f.topLevel || f.displayPath;
		if (!groups.has(groupKey)) groups.set(groupKey, []);
		groups.get(groupKey).push(f);
	}
	const out = [];
	for (const [groupKey, entries] of groups.entries()) {
		// If group has only one entry, render a bare <option> (no group overhead).
		if (entries.length === 1) {
			const f = entries[0];
			out.push(
				<option key={f.displayPath} value={f.displayPath}>
					{f.displayPath}
				</option>
			);
			continue;
		}
		out.push(
			<optgroup key={`grp-${groupKey}`} label={groupKey}>
				{entries.map((f) => (
					<option key={f.displayPath} value={f.displayPath}>
						{f.displayPath}
					</option>
				))}
			</optgroup>
		);
	}
	return out;
}

/**
 * One row of an IF clause: base-field selector + operator + operator-specific
 * value widget. The operator <select> is filtered by the selected field's
 * type — string-like fields only get const/enum/not_in/type_is_null, numeric
 * fields additionally get min/max/exclusive bounds, array fields get
 * contains_const. Changing the field resets the operator to the first
 * compatible one.
 */
export default function IfConditionRow({
	condition,
	condIndex,
	ruleIndex,
	availableFields,
	errors,
	onChange,
	onRemove,
}) {
	const testIdBase = `xallof-rule-${ruleIndex}-if-cond-${condIndex}`;
	const fieldMeta = availableFields.find(
		(f) => f.displayPath === condition.field
	);
	const fieldType = fieldMeta?.dataType || 'string';

	if (condition.field && !fieldMeta) {
		return (
			<DanglingRefRow
				scope="if-cond"
				ruleIndex={ruleIndex}
				condIndex={condIndex}
				missingName={condition.field}
				compatibleFields={availableFields}
				onReplace={(newName) => onChange({ ...condition, field: newName })}
				onRemove={onRemove}
			/>
		);
	}

	const operators = getAvailableOperators(fieldType);
	const operatorMeta = OPERATORS[condition.operator] || OPERATORS.const;
	const widget = operatorMeta.widget;

	const errorMsgs = errors || [];
	const errId = errorMsgs.length > 0 ? `${testIdBase}-err` : undefined;

	const handleFieldChange = (e) => {
		const next = e.target.value;
		const nextFieldMeta = availableFields.find((f) => f.displayPath === next);
		const nextOps = getAvailableOperators(nextFieldMeta?.dataType || 'string');
		const keepOp = nextOps.includes(condition.operator) ? condition.operator : nextOps[0];
		onChange({ ...condition, field: next, operator: keepOp, value: '' });
	};

	const handleOperatorChange = (e) => {
		const nextOp = e.target.value;
		const keepValue = OPERATORS[nextOp]?.needsValue
			&& OPERATORS[condition.operator]?.needsValue
			? condition.value
			: '';
		onChange({ ...condition, operator: nextOp, value: keepValue });
	};

	const handleValueChange = (v) => {
		onChange({ ...condition, value: v });
	};

	const handleRequiredChange = (e) => {
		onChange({ ...condition, required: e.target.checked });
	};

	return (
		<div
			className={`dsv-xallof-builder__if-cond-row${errorMsgs.length > 0 ? ' dsv-xallof-builder__if-cond-row--invalid' : ''}`}
			role="group"
			aria-label={`IF condition ${condIndex + 1}`}
			data-testid={`${testIdBase}`}
		>
			<div className="dsv-xallof-builder__if-cond-grid">
				<select
					id={`${testIdBase}-field`}
					className="dsv-xallof-builder__control"
					value={condition.field || ''}
					onChange={handleFieldChange}
					aria-label="Field"
					aria-describedby={errId}
					data-testid={`${testIdBase}-field`}
				>
					<option value="">Select field…</option>
					{renderGroupedFieldOptions(availableFields)}
				</select>

				<select
					id={`${testIdBase}-operator`}
					className="dsv-xallof-builder__control dsv-xallof-builder__control--op"
					value={condition.operator || 'const'}
					onChange={handleOperatorChange}
					aria-label="Operator"
					data-testid={`${testIdBase}-operator`}
				>
					{operators.map((op) => (
						<option key={op} value={op}>{OPERATORS[op].label}</option>
					))}
				</select>

				<div className="dsv-xallof-builder__value-slot">
					{widget === 'text' && (
						<input
							id={`${testIdBase}-value`}
							className="dsv-xallof-builder__control"
							type="text"
							value={condition.value ?? ''}
							onChange={(e) => handleValueChange(e.target.value)}
							placeholder="value"
							aria-label="Value"
							aria-describedby={errId}
							data-testid={`${testIdBase}-value`}
						/>
					)}
					{widget === 'csv' && (
						<input
							id={`${testIdBase}-value`}
							className="dsv-xallof-builder__control"
							type="text"
							value={Array.isArray(condition.value)
								? condition.value.join(', ')
								: (condition.value ?? '')}
							onChange={(e) => handleValueChange(e.target.value)}
							placeholder="a, b, c"
							aria-label="Values (comma separated)"
							aria-describedby={errId}
							data-testid={`${testIdBase}-value`}
						/>
					)}
					{widget === 'number' && (
						<input
							id={`${testIdBase}-value`}
							className="dsv-xallof-builder__control"
							type="number"
							value={condition.value ?? ''}
							onChange={(e) => handleValueChange(e.target.value)}
							placeholder="number"
							aria-label="Value"
							aria-describedby={errId}
							data-testid={`${testIdBase}-value`}
						/>
					)}
					{widget === 'none' && (
						<span className="dsv-xallof-builder__hint dsv-xallof-builder__value-slot--empty">
							no value needed
						</span>
					)}
				</div>

				<label
					className="dsv-xallof-builder__required-toggle"
					title="Adds this field to the if.required list"
				>
					<input
						type="checkbox"
						checked={condition.required !== false}
						onChange={handleRequiredChange}
						data-testid={`${testIdBase}-required`}
					/>
					<span>required</span>
				</label>

				<button
					type="button"
					className="dsv-xallof-builder__icon-btn dsv-xallof-builder__icon-btn--danger"
					onClick={onRemove}
					aria-label={`Remove condition ${condIndex + 1}`}
					title="Remove condition"
					data-testid={`${testIdBase}-delete`}
				>
					×
				</button>
			</div>

			{errorMsgs.length > 0 && (
				<span className="dsv-xallof-builder__error" id={errId}>
					{errorMsgs.join(' ')}
				</span>
			)}
		</div>
	);
}

IfConditionRow.propTypes = {
	condition: PropTypes.object.isRequired,
	condIndex: PropTypes.number.isRequired,
	ruleIndex: PropTypes.number.isRequired,
	availableFields: PropTypes.array.isRequired,
	errors: PropTypes.arrayOf(PropTypes.string),
	onChange: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
};
